import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import client from './api/client';
import { VALID_ACTORS, CHATGPT_PROJECT_URL } from './config';
import { AuthContext } from './auth/AuthContext';

import Summary from './components/Summary';
import LogForm from './components/LogForm';
import LogList from './components/LogList';
import SebastianTalk from './components/SebastianTalk';
import NotFound from './components/NotFound';

const RELOAD_THRESHOLD1_MS = 10 * 60 * 1000;
const RELOAD_THRESHOLD2_MS = 60 * 60 * 1000;
const OVERLAY_TIMEOUT_MS = 15 * 1000;
const SHOW_DELAY_MS = 80;
const MIN_VISIBLE_MS = 250;
const HIDE_GRACE_MS = 150;

export default function ActorView({ isAdmin }) {
  const { actor } = useParams();
  const isValidActor = VALID_ACTORS.includes(actor);

  const navigate = useNavigate();
  const { logout } = React.useContext(AuthContext);
  const { search } = useLocation();
  const qs = new URLSearchParams(search);

  // ids の取得（カンマ区切りを数値配列に）
  const idsParam = qs.get('ids');
  const ids = idsParam
    ? idsParam.split(',').map(s => Number(s)).filter(Number.isInteger)
    : null;

  const newFormDisabled = qs.get('new_form') === 'disable';
  const summaryDisabled = qs.get('summary') === 'disable';
  const entriesDisabled = qs.get('entries') === 'disable';

  // リフレッシュトリガー（子コンポーネントから更新時に呼ぶ）
  const [refreshFlag, setRefreshFlag] = React.useState(0);
  const handleRefresh = React.useCallback(() => setRefreshFlag(f => f + 1), []);

  // 画面内で共有するデータ（Summary と LogList で同じ「真実」を使う）
  const [summaryPayload, setSummaryPayload] = React.useState(null);
  const [logs, setLogs] = React.useState([]);
  const [loadError, setLoadError] = React.useState(null);

  // UIロック
  // uiBusy: 実際に通信が走っているか（厳密）
  // uiLocked: 入力コントロール無効化のための「人間向け」ロック（チラつき抑制）
  const [busyCount, setBusyCount] = React.useState(0);
  const uiBusy = busyCount > 0;
  const [uiLocked, setUiLocked] = React.useState(false);
  const [overlayTimedOut, setOverlayTimedOut] = React.useState(false);
  const [overlayVisible, setOverlayVisible] = React.useState(false);
  const shownAtRef = React.useRef(0);
  const showTimerRef = React.useRef(null);
  const hideTimerRef = React.useRef(null);

  // 入力ロック（uiLocked）用のタイマー
  const lockShownAtRef = React.useRef(0);
  const lockHideTimerRef = React.useRef(null);

  const beginBusy = React.useCallback(() => {
    setBusyCount(c => c + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      setBusyCount(c => Math.max(0, c - 1));
    };
  }, []);

  const lastRefreshRef = React.useRef(Date.now());
  const adjustedGuardRef = React.useRef({ actor: null, refreshFlag: null });

  // 画面暗転（overlayVisible）は「短い通信では出さない」+「点滅しない」
  React.useEffect(() => {
    // 後始末
    const clearTimers = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    if (uiBusy) {
      // 次の消灯予約があればキャンセル（点滅防止）
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      // 既に表示中なら何もしない
      if (overlayVisible) return;

      // 少し待ってから点灯（超短い通信は暗転しない）
      if (!showTimerRef.current) {
        showTimerRef.current = setTimeout(() => {
          showTimerRef.current = null;
          shownAtRef.current = Date.now();
          setOverlayVisible(true);
        }, SHOW_DELAY_MS);
      }
      return;
    }

    // uiBusy=false になった
    // 点灯待ち中ならキャンセル（暗転せずに済む）
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    if (!overlayVisible) return;

    const elapsed = Date.now() - shownAtRef.current;
    const remainMin = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const hideAfter = remainMin + HIDE_GRACE_MS;

    if (!hideTimerRef.current) {
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null;
        setOverlayVisible(false);
      }, hideAfter);
    }

    return clearTimers;
  }, [uiBusy, overlayVisible]);

  // 入力ロック（uiLocked）は「即時ロック」+「解除は暗転解除と揃える」
  // ※ SHOW_DELAY は無視（押下直後から無効化）
  React.useEffect(() => {
    const clearTimers = () => {
      if (lockHideTimerRef.current) {
        clearTimeout(lockHideTimerRef.current);
        lockHideTimerRef.current = null;
      }
    };

    if (uiBusy) {
      // 解除予約があればキャンセル（チラつき防止）
      if (lockHideTimerRef.current) {
        clearTimeout(lockHideTimerRef.current);
        lockHideTimerRef.current = null;
      }
      if (!uiLocked) {
        lockShownAtRef.current = Date.now();
        setUiLocked(true);
      }
      return;
    }

    // 通信は終わっているが、暗転中は入力もロックを維持（解除タイミングを揃える）
    if (overlayVisible) {
      return;
    }

    // 既に解除済みなら何もしない
    if (!uiLocked) {
      return;
    }

    const elapsed = Date.now() - lockShownAtRef.current;
    const remainMin = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const hideAfter = remainMin + HIDE_GRACE_MS;

    if (!lockHideTimerRef.current) {
      lockHideTimerRef.current = setTimeout(() => {
        lockHideTimerRef.current = null;
        setUiLocked(false);
      }, hideAfter);
    }

    return clearTimers;
  }, [uiBusy, overlayVisible, uiLocked]);

  const prefillInitValue = {
    recordType: 'activity',
    content: '',
    minutes: '',
    amount: ''
  };
  const [prefill, setPrefill] = React.useState(prefillInitValue);

  // ノルマ完了チェック時に呼ばれる
  const handleNormDone = React.useCallback((normTarget) => {
    if (!normTarget) return;
    setPrefill({ recordType: 'activity', content: normTarget });
    const element = document.getElementById('register-new');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  // タイトル更新（LogListから移管）
  React.useEffect(() => {
    if (!isValidActor) return;
    const pageTitle = isAdmin ? `${actor} (管理)` : actor;
    document.title = `セバスチャン - ${pageTitle}`;
  }, [actor, isAdmin, isValidActor]);

  // Summary + Logs をまとめて取得（パターン2）
  React.useEffect(() => {
    if (!isValidActor) return;

    let cancelled = false;
    const controller = new AbortController();
    const endBusy = beginBusy();

    (async () => {
      try {
        setOverlayTimedOut(false);
        setLoadError(null);

        const logsPath = isAdmin ? `/admin/logs/${actor}` : `/logs/${actor}`;
        const [summaryRes, logsRes] = await Promise.all([
          client.get(`/summary/${actor}`, { signal: controller.signal }),
          client.get(logsPath, { signal: controller.signal }),
        ]);

        if (cancelled) return;

        setSummaryPayload(summaryRes.data);
        setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
        lastRefreshRef.current = Date.now();

        // adjusted=true のときは1回だけ追加リフレッシュ（無限ループ回避）
        if (summaryRes.data?.adjusted) {
          const guard = adjustedGuardRef.current;
          if (guard.actor !== actor || guard.refreshFlag !== refreshFlag) {
            adjustedGuardRef.current = { actor, refreshFlag };
            setTimeout(() => handleRefresh(), 0);
          }
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        console.error(e);
        setLoadError(e);
      } finally {
        endBusy();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      endBusy();
    };
  }, [actor, isAdmin, isValidActor, refreshFlag, beginBusy, handleRefresh]);

  // 暗転が長引いた時のメッセージ（タイムアウト表示）
  React.useEffect(() => {
    if (!uiBusy) {
      setOverlayTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setOverlayTimedOut(true), OVERLAY_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [uiBusy]);

  // 自動リロード（復帰時に古いデータが残るのを防ぐ）
  React.useEffect(() => {
    const handleVisibility = () => {
      if (!isValidActor) return;
      if (document.visibilityState === 'visible') {
        const intervalMs = Date.now() - lastRefreshRef.current;
        if (intervalMs > RELOAD_THRESHOLD2_MS) {
          window.location.reload();
        } else if (intervalMs > RELOAD_THRESHOLD1_MS) {
          handleRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [handleRefresh, isValidActor]);

  if (!isValidActor) {
    return <NotFound />;
  }

  return (
    <div className={uiLocked ? 'is-ui-locked' : ''}>
      {overlayVisible && (
        <>
          <div className="loading-overlay" role="status" aria-live="polite" />
          <div className="loading-spinner" aria-hidden="true">
            <div className="spinner" />
            <div className="label">読み込み中...</div>
          </div>
          {overlayTimedOut && (
            <div className="loading-timeout">
              通信に時間がかかっています。電波状況をご確認ください。
            </div>
          )}
        </>
      )}

      {isAdmin && (
        <button className="logout" onClick={() => { logout(); navigate('/'); }}>
          ログアウト
        </button>
      )}

      <h1>
        <div className="img"><img src="/sebastian_logo.png" alt="Sebastian logo" /></div>
        <div className="title"><span>セバスチャン</span></div>
        <div className="subtitle"><span>-Sebastian-</span></div>
      </h1>
      <SebastianTalk
        actor={actor}
        logs={logs}
        summaryPayload={summaryPayload}
        projectUrl={CHATGPT_PROJECT_URL}
        uiLocked={uiLocked}
      />

      {!summaryDisabled && (
        <section>
          <h2>{actor} サマリー</h2>
          <Summary
            actor={actor}
            summaryPayload={summaryPayload}
            loading={uiLocked && !summaryPayload}
            error={loadError}
          />
        </section>
      )}

      {!newFormDisabled && (
        <section id="register-new">
          <h2>新規登録</h2>
          <LogForm
            actor={actor}
            onCreated={handleRefresh}
            prefill={prefill}
            clearPrefill={() => setPrefill({ ...prefillInitValue })}
            isAdmin={isAdmin}
            beginBusy={beginBusy}
            uiLocked={uiLocked}
          />
        </section>
      )}

      {!entriesDisabled && (
        <section>
          <h2>活動実績</h2>
          <LogList
            actor={actor}
            logs={logs}
            ids={ids}
            onRefresh={handleRefresh}
            onNormDone={handleNormDone}
            isAdmin={isAdmin}
            beginBusy={beginBusy}
            uiLocked={uiLocked}
          />
        </section>
      )}
    </div>
  );
}
